<template>
    <div class="layout-advanced-settings w-full">
        <RedesignBaseForm
            ref="generalSettingsForm"
            class="space-y-10"
        >
            <div class="w-full">
                <div class="uppercase font-semibold mb-2">
                    {{ $t('layout.config.appearance.settings') }}
                </div>
                <div class="text-sm">
                    {{ $t('layout.config.appearance.settings.info') }}
                </div>
            </div>
            <RedesignVerticalFormItem
                :title="getLayoutConfigParameterTranslation('ShowWidgetTitles')"
                class="layout-advanced-settings--item"
            >
                <RedesignSwitch
                    v-model="showWidgetTitlesModel"
                    active-value="1"
                    inactive-value="0"
                />
            </RedesignVerticalFormItem>
            <RedesignVerticalFormItem
                v-for="wColor in widgetColors"
                :key="wColor.LayoutParameterName"
                :title="getLayoutConfigParameterTranslation(wColor.LayoutParameterName)"
                class="layout-advanced-settings--item"
            >
                <RedesignWidgetColor
                    v-model="wColor.Value"
                    size="medium"
                />
            </RedesignVerticalFormItem>
            <RedesignVerticalFormItem
                v-for="fontItem in WIDGET_FONTS"
                :key="fontItem.key"
                :title="String($t(fontItem.label))"
                class="layout-advanced-settings--item"
            >
                <LayoutFontSettings
                    v-model="localParametersModel"
                    :settings="fontItem"
                />
            </RedesignVerticalFormItem>
            <div class="w-full">
                <div class="uppercase font-semibold mb-2">
                    {{ $t('layout.config.title.indicatorsColors') }}
                </div>
                <div class="text-sm">
                    {{ $t('layout.config.title.indicatorsColors.info') }}
                </div>
            </div>
            <RedesignVerticalFormItem
                v-for="wColor in widgetIndicatorColors"
                :key="wColor.LayoutParameterName"
                :title="getLayoutConfigParameterTranslation(wColor.LayoutParameterName)"
                class="layout-advanced-settings--item"
            >
                <RedesignWidgetColor
                    v-model="wColor.Value"
                    size="medium"
                />
            </RedesignVerticalFormItem>
            <div class="w-full">
                <div class="uppercase font-semibold mb-2">
                    {{ $t('layout.config.otherColors') }}
                </div>
            </div>
            <RedesignVerticalFormItem
                v-for="wColor in widgetOtherColors"
                :key="wColor.LayoutParameterName"
                :title="getLayoutConfigParameterTranslation(wColor.LayoutParameterName)"
                class="layout-advanced-settings--item"
            >
                <RedesignWidgetColor
                    v-model="wColor.Value"
                    size="medium"
                />
            </RedesignVerticalFormItem>
        </RedesignBaseForm>
    </div>
</template>

<script setup lang="ts">
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import { getLayoutConfigParameterTranslation } from '@/helpers/translation'
import RedesignVerticalFormItem from '@/components/RedesignBaseComponents/Form/RedesignVerticalFormItem.vue'
import RedesignSwitch from '@/components/RedesignBaseComponents/Inputs/RedesignSwitch.vue'
import { ILayoutItem } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'
import { computed } from 'vue'
import { getLayoutParameterByName } from '@/composables/globalData/useLayouts'
import RedesignWidgetColor from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import LayoutFontSettings, { PropsSettingType } from '@/components/RedesignComponents/Layout/Common/LayoutFontSettings.vue'
import { layoutDefaultColors } from '@/enum/colors'
import { getFilteredLayoutParameters, LAYOUT_PARAMETERS_DEFAULTS } from '@/enum/default-layout-config'

const WIDGET_COLORS = [
    'ColorWidgetHeaderText',
    'ColorWidgetBackground',
    'WidgetBorderColor',
    'ColorTableHeaderBg',
]

const WIDGET_FONTS: Array<PropsSettingType> = [
    {
        key: 'widgetContentText',
        colorParameterName: 'ColorTableHeaderText',
        fontParameterName: 'WidgetTableHeaderFontSize',
        label: 'layout.config.WidgetTableHeaderFontSize',
        defaultSize: 16,
        defaultColor: layoutDefaultColors.colorTableHeaderText
    },
    {
        key: 'tableContentText',
        colorParameterName: 'ColorTableContentText',
        fontParameterName: 'WidgetTableContentFontSize',
        label: 'layout.config.widgetTableContentFontSize',
        defaultSize: 16,
        defaultColor: layoutDefaultColors.mainTextColor
    },
    {
        key: 'widgetHeaderText',
        colorParameterName: 'ColorWidgetHeaderText',
        fontParameterName: 'WidgetTitlesFontSize',
        label: 'layout.config.widgetTitlesFontSize',
        defaultSize: 22,
        defaultColor: layoutDefaultColors.mainTextColor
    }
]

const INDICATORS_COLORS = [
    'OnHoldColor',
    'InCallColor',
    'DialingColor',
    'HoldTimeLimitColor',
    'HoldTimeWarningColor',
    'ActiveCallLimitColor',
    'ActiveCallWarningColor'
]

const OTHER_COLORS = [
    'CardsBgColor',
    'ItemsSecondaryColor',
    'ItemsMainColor',
    'ColorWidgetGroupFrames',
    'ColorWidgetGroupBackground'
]

const props = defineProps<{
    value: ILayoutItem
}>()
const emit = defineEmits<{
    (e: 'input', value: ILayoutItem): void
    (e: 'update:value', value: ILayoutItem): void
}>()

const layoutLocalModel = useVModel(props, 'value', emit, { eventName: 'input' })
const localParametersModel = computed({
    get () {
        return layoutLocalModel.value.LayoutParametersList
    },
    set (value) {
        layoutLocalModel.value = {
            ...layoutLocalModel.value,
            LayoutParametersList: [ ...value ]
        }
    }
})
const showWidgetTitlesModel = computed({
    get () {
        return getLayoutParameterByName(layoutLocalModel.value?.LayoutParametersList || [], 'ShowWidgetTitles', 'Value')
    },
    set (val) {
        const parameter = getLayoutParameterByName(layoutLocalModel.value?.LayoutParametersList || [], 'ShowWidgetTitles')
        if (!parameter) {
            layoutLocalModel.value.LayoutParametersList = [
                ...layoutLocalModel.value.LayoutParametersList,
                {
                    ...LAYOUT_PARAMETERS_DEFAULTS['ShowWidgetTitles'],
                    Value: val
                }
            ]
        } else {
            parameter.Value = val
        }
    }
})
const widgetColors = computed(() => {
    return getFilteredLayoutParameters(localParametersModel.value || [], WIDGET_COLORS)
})
const widgetIndicatorColors = computed(() => {
    return getFilteredLayoutParameters(localParametersModel.value || [], INDICATORS_COLORS)
})
const widgetOtherColors = computed(() => {
    return getFilteredLayoutParameters(localParametersModel.value || [], OTHER_COLORS)
})
</script>

